import type { ReactElement, ReactNode } from 'react';

import { AdvertisementPublicData } from '@cineamo/api/helpers/advertisement-helper';

// Where an advertisement's creative should link to, resolved once so every host app's `renderLink`
// only has to pick a rendering strategy (e.g. a locale-aware, typed-route `Link`) — not re-derive
// the precedence: an explicit clickThroughUrl always wins over a linked movie's program page, and
// an ad with neither renders unlinked.
export type AdvertisementLinkTarget =
    | { kind: 'url'; href: string }
    | { kind: 'content'; contentId: string }
    | { kind: 'none' };

export function resolveAdvertisementLinkTarget(
    advertisement: Pick<AdvertisementPublicData, 'clickThroughUrl' | 'contentId'>
): AdvertisementLinkTarget {
    if (advertisement.clickThroughUrl) {
        return { kind: 'url', href: advertisement.clickThroughUrl };
    }
    if (advertisement.contentId) {
        return { kind: 'content', contentId: String(advertisement.contentId) };
    }
    return { kind: 'none' };
}

export type AdvertisementLinkProps = {
    target: AdvertisementLinkTarget;
    onClick: () => void;
    className?: string;
    children: ReactNode;
};

export type AdvertisementRenderLink = (props: AdvertisementLinkProps) => ReactElement;

// Plain `<a>` fallback for hosts that don't need a locale-aware, typed-route `Link` component.
// Hosts using next-intl's typed `pathnames` (e.g. cinema-website's `/program/[contentId]`) must
// inject their own `renderLink` so the program link is localized correctly (a bare string href
// bypasses next-intl's per-locale path translation).
export const DefaultAdvertisementLink: AdvertisementRenderLink = ({ target, onClick, className, children }) => {
    if (target.kind === 'none') {
        return <>{children}</>;
    }
    const href = target.kind === 'url' ? target.href : `/program/${target.contentId}`;
    return (
        <a
            href={href}
            onClick={onClick}
            className={className}
            rel={target.kind === 'url' ? 'sponsored noopener noreferrer' : undefined}>
            {children}
        </a>
    );
};
